import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavBar, Toast, Dialog, Skeleton, Button, Uploader, Image, Grid, Empty, Field, ImagePreview } from 'react-vant';
import { ArrowLeft, PhotoO, DeleteO, Plus } from '@react-vant/icons';
import { getAlbum, uploadActivityPhoto, addPhotoRecord, deletePhoto, getSessionDetail } from '../../../api/club';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

const ClubAlbum = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { userInfo } = useSelector(state => state.user);
  const [photos, setPhotos] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [longPressTimer, setLongPressTimer] = useState(null);
  
  // 用于处理多选上传防抖
  const pendingFilesRef = React.useRef([]);
  const uploadTimerRef = React.useRef(null);
  
  // 权限计算
  const isAdmin = userInfo?.role === 'admin';
  const isInstructor = session && session.club?.instructor_id === userInfo?.id;
  const canUpload = isAdmin || isInstructor;

  useEffect(() => {
    fetchData();
  }, [sessionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [albumRes, sessionRes] = await Promise.all([
        getAlbum(sessionId),
        getSessionDetail(sessionId)
      ]);
      setPhotos(albumRes || []);
      setSession(sessionRes);
    } catch (error) {
      Toast.fail('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (targetFiles) => {
    if (!targetFiles || targetFiles.length === 0) return;

    setUploading(true);
    let successCount = 0;
    const total = targetFiles.length;

    try {
      for (let i = 0; i < total; i++) {
        const fileObj = targetFiles[i].file || targetFiles[i];
        if (!fileObj || !(fileObj instanceof File)) continue;

        const toast = Toast.loading({ 
          message: `正在上传 (${i + 1}/${total})...`, 
          forbidClick: true,
          duration: 0 
        });

        try {
          const formData = new FormData();
          formData.append('file', fileObj);
          const uploadRes = await uploadActivityPhoto(formData);
          
          await addPhotoRecord({
            session_id: parseInt(sessionId),
            image_url: uploadRes.url,
            description: (title || '社团活动记录').trim()
          });
          successCount++;
        } catch (err) {
          console.error(`第 ${i+1} 张照片上传失败:`, err);
        } finally {
          toast.clear();
        }
      }
      
      if (successCount > 0) {
        Toast.success(`成功上传 ${successCount} 张照片`);
        setTitle('');
        fetchData();
      } else {
        Toast.fail('上传失败，请检查网络');
      }
    } catch (error) {
      Toast.fail('操作异常');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (id) => {
    Dialog.confirm({
      title: '删除确认',
      message: '确定要删除这张照片吗？',
      onConfirm: async () => {
        try {
          await deletePhoto(id);
          Toast.success('已删除');
          fetchData();
        } catch (error) {
          Toast.fail('删除失败');
        }
      }
    });
  };

  const handleBatchDelete = () => {
    if (selectedIds.length === 0) return;
    Dialog.confirm({
      title: '批量删除',
      message: `确定要删除选中的 ${selectedIds.length} 张照片吗？`,
      onConfirm: async () => {
        const toast = Toast.loading({ message: '正在删除...', forbidClick: true });
        try {
          for (const id of selectedIds) {
            await deletePhoto(id);
          }
          Toast.success('已批量删除');
          setIsSelectMode(false);
          setSelectedIds([]);
          fetchData();
        } catch (error) {
          Toast.fail('部分照片删除失败');
          fetchData();
        } finally {
          toast.clear();
        }
      }
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.length === photos.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(photos.map(p => p.id));
    }
  };

  const onTouchStart = (photo) => {
    if (isSelectMode) return;
    const timer = setTimeout(() => {
      setIsSelectMode(true);
      setSelectedIds([photo.id]);
    }, 600); // 600ms 长按
    setLongPressTimer(timer);
  };

  const onTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff', paddingBottom: '80px', fontFamily: '"Inter", "PingFang SC", sans-serif' }}>
      <NavBar 
        title={<span style={{ fontWeight: 800 }}>{isSelectMode ? `已选中 ${selectedIds.length}` : '社团相册'}</span>} 
        leftText={isSelectMode ? <span style={{ color: '#056b4f' }}>取消</span> : <ArrowLeft size={20} />} 
        onClickLeft={() => {
          if (isSelectMode) {
            setIsSelectMode(false);
            setSelectedIds([]);
          } else {
            navigate(-1);
          }
        }}
        rightText={isSelectMode ? (
          <div style={{ display: 'flex', gap: 12 }}>
            <span onClick={handleSelectAll} style={{ color: '#056b4f' }}>{selectedIds.length === photos.length ? '不选' : '全选'}</span>
            <span onClick={handleBatchDelete} style={{ color: selectedIds.length > 0 ? '#e11d48' : '#cbd5e1' }}>删除</span>
          </div>
        ) : null}
        fixed
        placeholder
        border={false}
      />

      {session && !isSelectMode && (
        <div style={{ padding: '24px 16px', background: 'linear-gradient(180deg, #fff 0%, #f8fafc 100%)' }}>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px' }}>
            {session.club_name}
          </div>
        </div>
      )}

      <div style={{ padding: '2px' }}>
        {loading ? (
          <div style={{ padding: '20px' }}><Skeleton title row={10} /></div>
        ) : photos.length === 0 ? (
          <div style={{ marginTop: '100px' }}>
            <Empty description="相册空空如也，快来记录精彩瞬间吧" />
          </div>
        ) : (
          <Grid columnNum={3} gutter={2} border={false}>
            {photos.map(item => {
              const isSelected = selectedIds.includes(item.id);
              return (
                <Grid.Item key={item.id} style={{ height: '33.3vw' }}>
                  <div 
                    style={{ position: 'relative', width: '100%', height: '100%' }}
                    onTouchStart={() => onTouchStart(item)}
                    onTouchEnd={onTouchEnd}
                    onTouchMove={onTouchEnd}
                  >
                    <Image 
                      src={item.image_url} 
                      fit="cover" 
                      width="100%"
                      height="100%"
                      onClick={() => {
                        if (isSelectMode) {
                          toggleSelect(item.id);
                        } else {
                          ImagePreview.open({ images: photos.map(p => p.image_url), startPosition: photos.indexOf(item) });
                        }
                      }}
                    />
                    {isSelectMode && (
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: isSelected ? 'rgba(5, 107, 79, 0.2)' : 'rgba(0,0,0,0.1)',
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
                        padding: 4, pointerEvents: 'none'
                      }}>
                        <div style={{ 
                          width: 20, height: 20, borderRadius: '50%', 
                          background: isSelected ? '#056b4f' : 'rgba(255,255,255,0.5)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: isSelected ? 'none' : '1px solid #fff'
                        }}>
                          {isSelected && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}
                        </div>
                      </div>
                    )}
                    {!isSelectMode && (isAdmin || userInfo?.id === item.uploader_id) && (
                      <div 
                        onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                        style={{ 
                          position: 'absolute', top: 4, right: 4, 
                          background: 'rgba(0,0,0,0.3)', borderRadius: '50%', 
                          width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backdropFilter: 'blur(4px)'
                        }}
                      >
                        <DeleteO color="#fff" size={14} />
                      </div>
                    )}
                  </div>
                </Grid.Item>
              );
            })}
          </Grid>
        )}
      </div>

      {/* 悬浮上传按钮 */}
      {canUpload && !isSelectMode && (
        <div style={{ position: 'fixed', bottom: '100px', right: '20px', zIndex: 100 }}>
          <Uploader 
            multiple
            onChange={(files) => {
              if (uploading) return;

              // 清除之前的定时器，重新累计文件
              if (uploadTimerRef.current) {
                clearTimeout(uploadTimerRef.current);
              }

              pendingFilesRef.current = files;

              // 设置一个短延迟，等待所有 onChange 触发完成
              uploadTimerRef.current = setTimeout(() => {
                const finalFiles = pendingFilesRef.current;
                if (finalFiles && finalFiles.length > 0) {
                  Dialog.confirm({
                    title: '上传确认',
                    message: `确定上传这 ${finalFiles.length} 张照片到相册吗？`,
                    onConfirm: () => handleUpload(finalFiles)
                  });
                }
                uploadTimerRef.current = null;
              }, 100);
            }}
            previewImage={false}
          >
            <div style={{ 
              width: 56, height: 56, borderRadius: '28px', 
              background: 'linear-gradient(135deg, #055e43, #0c8c68)', 
              boxShadow: '0 8px 20px rgba(5, 107, 79, 0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
            }}>
              <Plus size={28} />
            </div>
          </Uploader>
        </div>
      )}
    </div>
  );
};

export default ClubAlbum;
